import { useState } from "react";
import { Sparkles, X, ArrowRight, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useLockBodyScroll } from "@/hooks/use-lock-body-scroll";
import { APP_VERSION } from "@/lib/constants";

const highlights = [
  {
    title: "Disregard an Unposted Brief",
    desc: "Toolbox Grooming",
    detail: "A Project Manager or Admin can disregard a held, held-back or failed brief that never reached ADO. It is kept for the record but hidden and never counted, and its agenda item goes back to needs brief, so the next Prepare briefs run writes a fresh one.",
    hue: "from-rose-500/[0.04] to-transparent",
  },
  {
    title: "Undo for 15 Minutes",
    desc: "Everything put back as it was",
    detail: "The toast after a disregard offers Undo. It restores the brief and its agenda items exactly, unless a newer brief was saved or a run picked the item up since.",
    hue: "from-amber-500/[0.04] to-transparent",
  },
  {
    title: "Agendas Editable Until the Meeting",
    desc: "Prepare briefs no longer locks",
    detail: "An agenda, prepared or not, can be edited until its meeting day ends in the pattern's time zone: add, remove and move items, and change the date. After that it is a locked record.",
    hue: "from-emerald-500/[0.04] to-transparent",
  },
  {
    title: "Upcoming Meetings by Date",
    desc: "One agenda per meeting",
    detail: "Session screens list each upcoming meeting with its own agenda, with Move and Remove on prepared agendas. A completed agenda with newly queued items runs again for just those.",
    hue: "from-sky-500/[0.04] to-transparent",
  },
  {
    title: "Confirm Guessed Values",
    desc: "Toolbox ADO Formulator",
    detail: "A value Claude guessed from the opening line shows as To confirm and does not count as in place until the person confirms it. Optional details are offered once before the review.",
    hue: "from-purple-500/[0.04] to-transparent",
  },
  {
    title: "Arrives With the Next Toolbox",
    desc: "Formulator 4, grooming 5",
    detail: "This release adds the SpecTree side (formulator contract 4, grooming contract 5). The Toolbox release that uses them ships next; older Toolbox versions keep working.",
    hue: "from-cyan-500/[0.04] to-transparent",
  },
];

export function WhatsNewCard() {
  const navigate = useNavigate();
  const [showPanel, setShowPanel] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  useLockBodyScroll(showPanel);

  return (
    <>
      {/* Dashboard card — Fey style with glow */}
      <div
        className="relative group cursor-pointer rounded-2xl overflow-hidden"
        onClick={() => { setShowPanel(true); }}
      >
        {/* Animated gradient glow underlay */}
        <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-br from-emerald-500/30 via-cyan-500/20 to-purple-500/30 opacity-50 group-hover:opacity-80 transition-opacity duration-500 blur-[1px]" />
        <div className="absolute -inset-[1px] rounded-2xl animate-gradient-shift bg-[length:200%_200%] bg-gradient-to-br from-emerald-500/15 via-transparent to-purple-500/15" />

        <div className="relative glass-card rounded-2xl p-5 border-0 space-y-3 hover:bg-white/[0.04] transition-colors">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/[0.06] border border-white/[0.08] rounded-full px-3 py-1.5">
            <Sparkles className="h-3.5 w-3.5 text-foreground" />
            <span className="text-xs font-medium text-foreground">What&apos;s New</span>
          </div>

          {/* Summary text */}
          <p className="text-[15px] leading-relaxed text-foreground/90">
            SpecTree {APP_VERSION} lets Toolbox Grooming disregard an unposted brief (with Undo), keeps
            agendas editable until the meeting date passes, and has the ADO Formulator confirm values it
            guessed from the opening line (with the next Toolbox release).
          </p>

          {/* Source label */}
          <p className="text-xs text-muted-foreground">
            SpecTree · {APP_VERSION}
          </p>
        </div>
      </div>

      {/* Right overlay panel */}
      <AnimatePresence>
        {showPanel && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
            className="fixed top-4 right-4 bottom-4 w-[calc(100vw-2rem)] sm:w-[420px] z-50 glass-card rounded-2xl overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06]">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-foreground" />
                <h2 className="text-sm font-semibold">What&apos;s New — {APP_VERSION}</h2>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); setShowPanel(false); }}
                className="h-7 w-7 rounded-lg flex items-center justify-center hover:bg-white/[0.06] transition-colors"
              >
                <X className="h-4 w-4 text-muted-foreground" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-3">
              {highlights.map((h, i) => {
                const isOpen = expandedIndex === i;
                return (
                  <motion.div
                    key={h.title}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.06, duration: 0.2 }}
                    className="glass-card rounded-lg overflow-hidden cursor-pointer relative"
                    onClick={() => { setExpandedIndex(isOpen ? null : i); }}
                  >
                    <div className={`absolute inset-0 bg-gradient-to-r ${h.hue} pointer-events-none rounded-lg`} />
                    <div className="flex items-center justify-between p-4">
                      <div>
                        <h3 className="text-sm font-medium">{h.title}</h3>
                        <p className="text-xs text-muted-foreground mt-0.5">{h.desc}</p>
                      </div>
                      <motion.div
                        animate={{ rotate: isOpen ? 180 : 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                      </motion.div>
                    </div>
                    <AnimatePresence>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2, ease: 'easeInOut' }}
                          className="overflow-hidden"
                        >
                          <div className="px-4 pb-4 border-t border-white/[0.04] pt-3">
                            <p className="text-xs text-muted-foreground leading-relaxed">{h.detail}</p>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-white/[0.06]">
              <button
                onClick={() => { setShowPanel(false); void navigate("/whats-new"); }}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
              >
                View full changelog <ArrowRight className="h-3 w-3" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Backdrop */}
      <AnimatePresence>
        {showPanel && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-40 bg-black/40"
            onClick={() => { setShowPanel(false); }}
          />
        )}
      </AnimatePresence>
    </>
  );
}
